<template>
  <i-modal
    :title="openedImage.isNew ? __('Image Insert') : __('Edit Image')"
    :style="{ maxWidth: '900px' }"
    :closeOnClickAway="false"
    @openAutoFocus="syncImage"
    v-model:show="show"
    @keydown.enter.prevent="addImage(() => emit('update:show', false))"
  >
    <template #default>
      <div class="tabs">
        <button @click="activeTab = 'image'" :class="{ active: activeTab === 'image' }">
          {{ __('Image Info') }}
        </button>
        <button @click="activeTab = 'advanced'" :class="{ active: activeTab === 'advanced' }">
          {{ __('Advanced') }}
        </button>
        <button @click="activeTab = 'link'" :class="{ active: activeTab === 'link' }">
          {{ __('Link') }}
        </button>
      </div>

      <form class="modal-form">
        <!-- Image Info Tab -->
        <div v-if="activeTab === 'image'" class="tab-content">
          <div class="form-flex">
            <label>{{ __('Title') }}</label>
            <input ref="firstInput" type="text" v-model.trim="openedImage.attr.alt" autocomplete="off" />

            <label>{{ __('URL') }}</label>
            <input type="text" v-model.trim="openedImage.attr.src" autocomplete="off" />

            <div class="dimensions">
              <div class="width-combo">
                <label>
                  {{ __('Width') }}
                  <i-icon
                    :icon="lockRatio ? 'lock' : 'lock-open'"
                    :title="lockRatio ? __('Unlock Ratio') : __('Lock Ratio')"
                    @click.prevent="toggleRatio()"
                    aria-role="button"
                    type="fas"
                    size="small"
                  ></i-icon>
                </label>
                <input
                  type="number"
                  v-model.trim.number="openedImage.attr.width"
                  @blur.prevent="syncRatio('width')"
                  autocomplete="off"
                />
              </div>

              <div class="height-combo">
                <label>
                  {{ __('Height') }}
                  <i-icon
                    :icon="lockRatio ? 'lock' : 'lock-open'"
                    :title="lockRatio ? __('Unlock Ratio') : __('Lock Ratio')"
                    @click.prevent="toggleRatio()"
                    aria-role="button"
                    type="fas"
                    size="small"
                  ></i-icon>
                </label>
                <input
                  type="number"
                  v-model.trim.number="openedImage.attr.height"
                  @blur.prevent="syncRatio('height')"
                  autocomplete="off"
                />
              </div>
            </div>

            <label>{{ __('Link Node ID') }}</label>
            <input type="text" v-model.trim="openedImage.attr['data-node-id']" autocomplete="off" />

            <label>{{ __('Caption') }}</label>
            <input type="text" v-model.trim="openedImage.attr.caption" autocomplete="off" />

            <label>{{ __('Wraparound') }}</label>
            <div class="radio-group">
              <label><input type="radio" value="" v-model="align" />{{ __('None') }}</label>
              <label
                ><input type="radio" value="left" v-model="align" />{{ __('Align Left') }}</label
              >
              <label
                ><input type="radio" value="center" v-model="align" />{{
                  __('Align Center')
                }}</label
              >
              <label
                ><input type="radio" value="right" v-model="align" />{{ __('Align Right') }}</label
              >
            </div>
            <div class="button-group">
              <button 
                class="btn-action" 
                @click.prevent="callInsertionWindow('image')"
                @keydown.enter.prevent.stop="callInsertionWindow('image')"
              >
                <i-icon :icon="`folder`" type="far" size="small"></i-icon>
                {{ __('Browse MAM') }}
              </button>
              <button 
                class="btn-action right" 
                @click.prevent="resetOpenedImage"
                @keydown.enter.prevent.stop="resetOpenedImage"
              >
                <i-icon :icon="`refresh`" type="far" size="small"></i-icon>
                {{ __('Unset image') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Advanced Tab -->
        <div v-if="activeTab === 'advanced'" class="tab-content">
          <div class="form-flex">
            <label>{{ __('ID') }}</label>
            <input type="text" v-model.trim="openedImage.attr.id" autocomplete="off" />

            <label>{{ __('Class') }}</label>
            <input type="text" v-model.trim="openedImage.attr.class" autocomplete="off" />

            <label>{{ __('Long Description') }}</label>
            <input type="text" v-model.trim="openedImage.attr.longdesc" autocomplete="off" />

            <label>{{ __('Advisory Title') }}</label>
            <input type="text" v-model.trim="openedImage.attr.title" autocomplete="off" />

            <label>{{ __('Style') }}</label>
            <input type="text" v-model.trim="openedImage.attr.style" autocomplete="off" />
          </div>
        </div>

        <!-- Link Tab -->
        <div v-if="activeTab === 'link'" class="tab-content">
          <div class="form-flex">
            <label>{{ __('Target') }}</label>
            <Dropdown style="margin-bottom: 15px" :item-count="linkTargets.length">
              <template #trigger="{ toggle, attrs }">
                <span class="i-dropdown-wrapper">
                  <i-button class="link-modal-dropdown" @click.prevent="toggle" v-bind="attrs">
                    <span class="tiptap-select-label">{{
                      activeLabel(openedLink.attr.target, linkTargets)?.label ||
                      openedLink.attr.target
                    }}</span>
                    <i-icon
                      icon="caret-down"
                      :style="{ fontSize: '0.6em' }"
                      :title="__('More options')"
                    ></i-icon>
                  </i-button>
                </span>
              </template>
              <template #default="{ isOpen, close, activeIndex }">
                <ul class="dropdown-menu i-dropdown-menu block" role="select">
                  <div class="pl2">{{ __('Select the link target') }}</div>
                  <li
                    v-for="(item, index) in linkTargets"
                    :key="index"
                    :class="[
                      'dropdown-item',
                      { 'i-dropdown-item-selected': item.value === openedLink.attr.target }
                    ]"
                    :aria-label="item.label"
                    v-dropdown-focus="{ index, activeIndex }"
                  >
                    <a
                      href="#"
                      class="i-action-browse-by"
                      :title="item.label"
                      @click.prevent="selectLinkType(item, close)"
                      @keydown.enter.prevent.stop="selectLinkType(item, close)"
                    >
                      {{ item.label }}
                    </a>
                  </li>
                </ul>
              </template>
            </Dropdown>
            <input
              type="text"
              v-if="
                !activeLabel(openedLink.attr.target, linkTargets) ||
                activeLabel(openedLink.attr.target, linkTargets)?.value === ''
              "
              v-model.trim="openedLink.attr.target"
              autocomplete="off"
            />

            <label>{{ __('URL') }}</label>
            <input type="text" v-model.trim="openedLink.attr.href" autocomplete="off" />

            <label>{{ __('Link Node ID') }}</label>
            <input type="text" v-model.trim="openedLink.attr['data-node-id']" autocomplete="off" />
            <div class="button-group">
              <button 
                class="btn-action" 
                @click.prevent="callInsertionWindow('link')"
                @keydown.enter.prevent.stop="callInsertionWindow('image')"
              >
                <i-icon :icon="`folder`" type="far" size="small"></i-icon>
                {{ __('Browse MAM') }}
              </button>
              <button
                class="btn-action"
                @click.prevent="callInsertionWindow('link', '/document/browser')"
                @keydown.enter.prevent.stop="callInsertionWindow('link', '/document/browser')"
              >
                <i-icon :icon="`link`" type="far" size="small"></i-icon>
                {{ __('Imperia Link') }}
              </button>
              <button 
                class="btn-action right" 
                @click.prevent="resetOpenedLink"
                @keydown.enter.prevent.stop="resetOpenedLink"
              >
                <i-icon :icon="`refresh`" type="far" size="small"></i-icon>
                {{ __('Unset link') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Image Preview -->
        <div class="image-preview">
          <img
            :src="openedImage.attr?.src || DEFAULT_IMAGE_PLACEHOLDER"
            :width="openedImage.attr?.width || DEFAULT_IMAGE_WIDTH"
            :height="openedImage.attr?.height || DEFAULT_IMAGE_WIDTH"
            :alt="openedImage.attr?.alt"
            :id="openedImage.attr?.id"
            :class="openedImage.attr?.class"
            :longdesc="openedImage.attr?.longdesc"
            :title="openedImage.attr?.title"
            :style="{ aspectRatio: `${openedImage.attr.width}/${openedImage.attr.height}` }"
          />
        </div>
      </form>
    </template>

    <template #footer>
      <div>
        <i-button 
          variant="secondary" 
          @click.prevent="emit('update:show', false)"  
          @keydown.enter.prevent.stop="emit('update:show', false)"
        >
          {{__('Cancel')}}
        </i-button>
        <i-button variant="primary" @click.prevent="addImage(() => emit('update:show', false))">
          {{ openedImage.isNew ? __('Insert Image') : __('Apply Changes') }}
        </i-button>
      </div>
    </template>
  </i-modal>
</template>

<script setup>
import { ref, toRef, shallowRef, useAttrs, watch, computed } from 'vue'
import Dropdown from '@/components/lib/dropdown/Dropdown.vue'
import { useMamTiptap } from '@/composables/useMamTiptap'

const emit = defineEmits(['update:show'])
const props = defineProps({
  editor: { type: Object, required: true },
})
const show = defineModel('show', { type: Boolean, default: false })

// Keep a ref to the editor for the composable
const editorRef = shallowRef(props.editor)

// Composable (replaces mixin):
const {
  __,
  __x,
  DEFAULT_IMAGE_WIDTH,
  openedImage,
  openedLink,
  linkTargets,
  activeLabel,
  resetOpenedImage,
  resetOpenedLink,
  getEditorImage,
  getEditorLink,
  callInsertionWindow,
  addImage
} = useMamTiptap(editorRef)

// Local UI state
const firstInput = ref(null)
const activeTab = ref('image')
const lockRatio = ref(true)
const DEFAULT_IMAGE_PLACEHOLDER = '/imperia/images/gui/default11.svg'

// attrs.show watcher (replaces mixin watch on $attrs.show)
const attrs = useAttrs()
watch(
  () => attrs.show,
  (val) => {
    // The composable already wires global listeners in onMounted/onBeforeUnmount,
    // so here we only react when modal becomes visible.
    if (val) {
      // prepare state when modal opens
      syncImage()
    }
  },
  { immediate: false }
)

function selectLinkType(item, close) {
  item.method()
  close()
}

function toggleRatio(state) {
  if (state !== undefined) lockRatio.value = state
  else lockRatio.value = !lockRatio.value
}

function syncRatio(type) {
  if (!lockRatio.value) return
  if (!openedImage.width || !openedImage.height) return

  if (type === 'width') {
    if (openedImage.attr.width == 0) return
    openedImage.attr.height = Math.round(
      openedImage.height * (openedImage.attr.width / openedImage.width)
    )
  } else if (type === 'height') {
    if (openedImage.attr.height == 0) return
    openedImage.attr.width = Math.round(
      openedImage.width * (openedImage.attr.height / openedImage.height)
    )
  }
}

function parseStyle(styleStr) {
  return Object.fromEntries(
    (styleStr || '')
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [key, value] = s.split(':').map((part) => part.trim())
        return [key, value]
      })
  )
}
function buildStyle(styleObj) {
  return Object.entries(styleObj)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ')
}

const align = computed({
  get() {
    const styleStr = openedImage.attr.style || ''
    const styleObj = parseStyle(styleStr)
    if (styleObj.float === 'left') return 'left'
    if (styleObj.float === 'right') return 'right'
    if (
      styleObj.display === 'block' &&
      styleObj['margin-left'] === 'auto' &&
      styleObj['margin-right'] === 'auto'
    ) {
      return 'center'
    }
    return ''
  },
  set(newAlign) {
    const styleStr = openedImage.attr.style || ''
    const styleObj = parseStyle(styleStr)
    delete styleObj.float
    delete styleObj.display
    delete styleObj['margin-left']
    delete styleObj['margin-right']
    switch (newAlign) {
      case 'left':
        styleObj.float = 'left'
        break
      case 'right':
        styleObj.float = 'right'
        break
      case 'center':
        styleObj.display = 'block'
        styleObj['margin-left'] = 'auto'
        styleObj['margin-right'] = 'auto'
        break
      default:
        break
    }
    openedImage.attr.style = buildStyle(styleObj)
  }
})

function syncImage() {
  activeTab.value = 'image'
  if (firstInput?.value?.focus) {
    firstInput.value.focus();
  }

  toggleRatio(true)
  resetOpenedLink()
  getEditorImage()

  if (openedImage.attr.caption && openedImage.attr.link) {
    openedLink.attr = { ...openedImage.attr.link }
    openedLink.setUp = true
  } else if (!openedImage.attr.caption) {
    const { state } = editorRef.value
    const { selection, schema } = state
    const { from, to } = selection
    let isValid = false
    state.doc.nodesBetween(from, to, (node) => {
      const linkMark = schema.marks.link.isInSet(node.marks)
      if (linkMark) {
        if (node.childCount === 1 && node.firstChild.type === schema.nodes.image) {
          isValid = true
        }
      }
    })
    if (!isValid) {
      getEditorLink()
    }
  }
  openedImage.width = openedImage.attr.width
  openedImage.height = openedImage.attr.height
}
</script>

<style scoped>
/* (same styles as your original component) */
.tabs {
  display: flex;
  justify-content: flex-start;
  border-bottom: 1px solid #e5e5e5;
}
.tabs button {
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  color: #333;
}
.tabs button.active {
  font-weight: bold;
  border-bottom: 2px solid #007bff;
  color: #007bff;
}
.tabs button:hover {
  background-color: #e1e1e1;
}
.modal-form {
  display: flex;
  gap: 2rem;
  padding: 1.5rem;
  font-family: system-ui, sans-serif;
  font-size: 14px;
  background-color: #fff;
  border-radius: 12px;
}
.tab-content {
  flex: 1;
}
.form-flex {
  display: flex;
  flex-direction: column;
  grid-template-columns: 1fr 2fr;
  align-items: left;
}
.dimensions {
  display: flex;
  justify-content: space-between;
  gap: 15px;
}
.width-combo,
.height-combo {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.form-flex label {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin-bottom: 0px;
}
.form-flex input[type='text'],
.form-flex input[type='number'] {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  width: auto;
  font-size: 14px;
  margin-bottom: 15px;
}
.form-flex input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.btn-action {
  flex: 1;
  padding: 8px 8px;
  font-size: 13px;
  cursor: pointer;
  background-color: white;
  color: black;
  border: 1px solid #d1d5db;
  border-radius: 3px;
}
.btn-action:hover {
  background-color: #f3f4f6;
  border-color: #cbd5e1;
}
.image-preview {
  margin-top: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem;
  overflow: auto;
  max-width: 100%;
  width: 40%;
  background: #e5e7eb;
}
.image-preview img {
  max-width: 100%;
  height: auto;
  display: block;
}
.button-group {
  flex: 1;
  width: 100%;
  margin-top: 15px;
  display: flex;
  gap: 5px;
  justify-content: center;
}
.tiptap-select-label {
  margin-right: 5px;
}
.i-dropdown-wrapper {
  margin-bottom: 15px;
}
.i-dropdown-menu {
  display: block;
  overflow-y: auto;
  max-height: 200px;
  width: 120px;
}
.i-dropdown-menu .pl2 {
  width: 100%;
  padding: 4px;
  padding-left: 14px;
  background-color: lightgray;
  border-bottom: 1px solid gray;
}
.link-modal-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0px;
  font-size: 14px;
  background-color: white;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 3px !important;
  cursor: pointer;
  text-align: left;
}
.checkbox-group {
  display: flex;
  gap: 8px;
  padding-top: 4px;
  justify-content: space-between;
}
.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  font-weight: 400;
  cursor: pointer;
}
.checkbox-group input[type='radio'] {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
  margin: 0;
}
.radio-group {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  justify-content: space-between;
}
.radio-group label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
}
.radio-group input[type='radio'] {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
  transition: border 0.2s ease;
  margin: 0px;
}
.radio-group input[type='radio']:checked {
  border-color: #2563eb;
  background-color: #2563eb;
}
.radio-group input[type='radio']::before {
  content: '';
  display: block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: white;
  position: absolute;
  top: 4px;
  left: 4px;
  transform: scale(0);
  transition: transform 0.2s ease;
}
.radio-group input[type='radio']:checked::before {
  transform: scale(1);
}
</style>
